import type { QueryClient } from '@tanstack/vue-query'

const pending = new WeakMap<QueryClient, Map<string, Promise<void>>>()
const holds = new WeakMap<QueryClient, Map<string, { count: number; dirty: boolean }>>()
export function holdEpisodeRefresh(client: QueryClient, queryKey: string[]) {
  let scopes = holds.get(client)
  if (!scopes) { scopes = new Map(); holds.set(client, scopes) }
  const id = JSON.stringify(queryKey), state = scopes.get(id) || { count: 0, dirty: false }
  state.count++; scopes.set(id, state)
  return () => { if (--state.count === 0) { scopes!.delete(id); if (state.dirty) void refreshEpisodeQueries(client, queryKey).catch(() => undefined) } }
}
/** Merge REST and socket refreshes. Let earlier reads finish, then fetch fresh data. */
export function refreshEpisodeQueries(client: QueryClient, queryKey: string[]): Promise<void> {
  const held = holds.get(client)?.get(JSON.stringify(queryKey))
  if (held) { held.dirty = true; return Promise.resolve() }
  let requests = pending.get(client)
  if (!requests) { requests = new Map(); pending.set(client, requests) }
  const id = JSON.stringify(queryKey)
  const existing = requests.get(id)
  if (existing) return existing
  const refresh = new Promise<void>(resolve => setTimeout(resolve, 150)).then(async () => {
    const delayed = holds.get(client)?.get(JSON.stringify(queryKey))
    if (delayed) { delayed.dirty = true; requests!.delete(id); return }
    const reads = client.getQueryCache().findAll({ queryKey })
      .filter(query => query.state.fetchStatus === 'fetching').map(query => query.promise)
    await Promise.allSettled(reads)
    // New invalidations during this fetch queue one follow-up rather than cancelling it.
    requests!.delete(id)
    const resumed = holds.get(client)?.get(id)
    if (resumed) { resumed.dirty = true; return }
    await client.invalidateQueries({ queryKey }, { cancelRefetch: false })
  })
  requests.set(id, refresh)
  return refresh
}
